import {
  CELL_EMPTY,
  CELL_FILLED,
  CoverageGrid,
  cellsWhere,
} from 'app/utils/field-boundary/grid';
import { isPositiveNumber } from 'app/utils/field-boundary/shared';

/**
 * A transit corridor is one implement pass wide; the margin absorbs GPS drift
 * and back-and-forth passes that only partly overlap.
 */
const CORRIDOR_WIDTH_FACTOR = 1.25;

/** Cells the conservative rasterization may add on each side of a corridor. */
const RASTER_WIDENING_CELLS = 1;

/**
 * Corridors are a small share of the worked area. When removing them would
 * drop more than half of it, the field itself is narrow and is left untouched.
 */
const MIN_KEPT_AREA_FRACTION = 0.5;

/** Chamfer 3-4 distance steps: a cheap, close approximation of Euclidean distance. */
export const CHAMFER_ORTHOGONAL_STEP = 3;
const CHAMFER_DIAGONAL_STEP = 4;

/**
 * One pass of a square min/max filter along rows (`alongRows`) or columns,
 * using a sliding window count. Cells outside the grid count as empty.
 */
function filterLines(
  source: Uint8Array,
  grid: CoverageGrid,
  radius: number,
  alongRows: boolean,
  isDilation: boolean,
): Uint8Array {
  const target = new Uint8Array(source.length);
  let lineCount = grid.columns;
  let lineLength = grid.rows;
  let positionStride = grid.columns;
  let lineStride = 1;
  if (alongRows) {
    lineCount = grid.rows;
    lineLength = grid.columns;
    positionStride = 1;
    lineStride = grid.columns;
  }
  const windowSize = 2 * radius + 1;

  for (let line = 0; line < lineCount; line++) {
    const lineStart = line * lineStride;
    let filledInWindow = 0;
    for (
      let position = 0;
      position <= Math.min(radius, lineLength - 1);
      position++
    ) {
      filledInWindow += source[lineStart + position * positionStride];
    }
    for (let position = 0; position < lineLength; position++) {
      let isFilled = filledInWindow === windowSize;
      if (isDilation) {
        isFilled = filledInWindow > 0;
      }
      if (isFilled) {
        target[lineStart + position * positionStride] = CELL_FILLED;
      }
      const entering = position + radius + 1;
      const leaving = position - radius;
      if (entering < lineLength) {
        filledInWindow += source[lineStart + entering * positionStride];
      }
      if (leaving >= 0) {
        filledInWindow -= source[lineStart + leaving * positionStride];
      }
    }
  }
  return target;
}

function squareFilter(
  source: Uint8Array,
  grid: CoverageGrid,
  isDilation: boolean,
): Uint8Array {
  const radius = grid.closingRadiusCells;
  const horizontal = filterLines(source, grid, radius, true, isDilation);
  return filterLines(horizontal, grid, radius, false, isDilation);
}

/** Morphological closing: dilation followed by erosion with the same radius. */
export function closeGaps(grid: CoverageGrid): void {
  if (grid.closingRadiusCells > 0) {
    const dilated = squareFilter(grid.cells, grid, true);
    grid.cells = squareFilter(dilated, grid, false);
  }
}

/** Starting distances for a chamfer propagation: zero on the seeds. */
export function seedDistances(
  grid: CoverageGrid,
  isSeed: (index: number) => boolean,
): Int32Array {
  const distances = new Int32Array(grid.cells.length);
  const unreachable = CHAMFER_DIAGONAL_STEP * (grid.columns + grid.rows);
  for (let index = 0; index < distances.length; index++) {
    if (!isSeed(index)) {
      distances[index] = unreachable;
    }
  }
  return distances;
}

/**
 * Two-pass chamfer propagation of `distances`, in place. When `nearest` is
 * given, it also carries along which seed each cell is closest to.
 */
export function propagateChamfer(
  grid: CoverageGrid,
  distances: Int32Array,
  nearest: Int32Array | null,
): void {
  const { columns, rows } = grid;
  const relax = (index: number, neighbor: number, step: number) => {
    const candidate = distances[neighbor] + step;
    if (candidate < distances[index]) {
      distances[index] = candidate;
      if (nearest) {
        nearest[index] = nearest[neighbor];
      }
    }
  };

  for (let row = 0; row < rows; row++) {
    for (let column = 0; column < columns; column++) {
      const index = row * columns + column;
      if (column > 0) {
        relax(index, index - 1, CHAMFER_ORTHOGONAL_STEP);
      }
      if (row > 0) {
        relax(index, index - columns, CHAMFER_ORTHOGONAL_STEP);
        if (column > 0) {
          relax(index, index - columns - 1, CHAMFER_DIAGONAL_STEP);
        }
        if (column < columns - 1) {
          relax(index, index - columns + 1, CHAMFER_DIAGONAL_STEP);
        }
      }
    }
  }
  for (let row = rows - 1; row >= 0; row--) {
    for (let column = columns - 1; column >= 0; column--) {
      const index = row * columns + column;
      if (column < columns - 1) {
        relax(index, index + 1, CHAMFER_ORTHOGONAL_STEP);
      }
      if (row < rows - 1) {
        relax(index, index + columns, CHAMFER_ORTHOGONAL_STEP);
        if (column < columns - 1) {
          relax(index, index + columns + 1, CHAMFER_DIAGONAL_STEP);
        }
        if (column > 0) {
          relax(index, index + columns - 1, CHAMFER_DIAGONAL_STEP);
        }
      }
    }
  }
}

/** Chamfer steps from every cell to the nearest cell equal to `target`. */
function chamferDistance(
  grid: CoverageGrid,
  cells: Uint8Array,
  target: number,
): Int32Array {
  const distances = seedDistances(grid, (index) => cells[index] === target);
  propagateChamfer(grid, distances, null);
  return distances;
}

function countFilled(cells: Uint8Array): number {
  let count = 0;
  for (let index = 0; index < cells.length; index++) {
    count += cells[index];
  }
  return count;
}

/**
 * Cuts off transit corridors: stretches driven with the sections on between
 * the field and the road or the farmyard, about one implement wide.
 *
 * @remarks
 * Morphological opening with a disk slightly wider than a corridor, then a
 * reconstruction that brings back whatever lies within the same distance of
 * the remaining blocks, so their corners are not rounded off; a corridor leaves
 * at most a stub that long. Skipped when the implement width is unknown, and
 * when it would drop most of the area, which means the field itself is narrow.
 */
export function removeTransitCorridors(
  grid: CoverageGrid,
  implementWidthMeters: number | null | undefined,
): void {
  if (!isPositiveNumber(implementWidthMeters)) {
    return;
  }
  const corridorWidthCells =
    (implementWidthMeters * CORRIDOR_WIDTH_FACTOR) / grid.cellSizeMeters +
    2 * RASTER_WIDENING_CELLS;
  // Distances run between cell centers, so the middle of a strip `w` cells
  // wide lies (w + 1) / 2 cells away from the nearest empty cell.
  const radius = ((corridorWidthCells + 1) / 2) * CHAMFER_ORTHOGONAL_STEP;

  const worked = grid.cells;
  const toEmpty = chamferDistance(grid, worked, CELL_EMPTY);
  const eroded = cellsWhere(
    grid,
    (index) => worked[index] === CELL_FILLED && toEmpty[index] > radius,
  );
  const toEroded = chamferDistance(grid, eroded, CELL_FILLED);
  const opened = cellsWhere(grid, (index) => toEroded[index] <= radius);
  const toCore = chamferDistance(grid, opened, CELL_FILLED);
  const reconstructed = cellsWhere(
    grid,
    (index) => worked[index] === CELL_FILLED && toCore[index] <= radius,
  );

  if (
    countFilled(reconstructed) >=
    countFilled(worked) * MIN_KEPT_AREA_FRACTION
  ) {
    grid.cells = reconstructed;
  }
}
